// Reusable 80mm thermal receipt component - used for both live preview and print
import React from "react";
import { useSettings } from "../context/SettingsContext";
import { formatHebrewDate, format12HourHeb } from "../lib/hebrewDate";
import { getReasonCategory, CATEGORY_LABEL_HE, CATEGORY_JUSTIFIED } from "../lib/reasons";

export default function ReceiptView({ pass, previewMode = false }) {
  const { settings } = useSettings();

  if (!pass) {
    return (
      <div className="w-[80mm] mx-auto bg-white shadow rounded p-6 text-center text-slate-400">
        <div className="text-sm">Preview receipt will appear here</div>
        <div className="text-xs mt-1">Fill in the form to see the receipt</div>
      </div>
    );
  }

  const created = pass.createdAt ? new Date(pass.createdAt) : new Date();
  const schoolName = settings?.schoolName || "תלמוד תורה אור התורה";
  const showLogo = settings?.showLogoOnReceipt !== false;
  const showSig = settings?.showSignatureLine !== false;
  const footer = settings?.receiptFooter || "";
  const schoolAddress = settings?.schoolAddress || "";
  const schoolPhone = settings?.schoolPhone || "";

  return (
    <div
      dir="rtl"
      className={`receipt bg-white text-black ${previewMode ? "shadow-xl rounded-lg" : "shadow-lg"} p-4`}
      style={{ width: "80mm", fontFamily: "Rubik, monospace" }}
    >
      {/* Header */}
      <div className="text-center border-b-2 border-dashed border-black pb-3 mb-3">
        {showLogo && (
          <img src="/assets/ohr-hatorah-logo.png" alt="" className="w-20 h-20 object-contain mx-auto mb-1" />
        )}
        <div className="font-serif-heb text-xl font-bold" style={{ fontFamily: "Frank Ruhl Libre, serif" }}>{schoolName}</div>
        {(schoolAddress || schoolPhone) && (
          <div className="text-[10px] text-slate-600 mt-1" dir="ltr">
            {schoolAddress}{schoolAddress && schoolPhone ? " · " : ""}{schoolPhone}
          </div>
        )}
      </div>

      {/* Title */}
      <div className="text-center mb-3">
        <div className="font-serif-heb text-3xl font-bold" style={{ fontFamily: "Frank Ruhl Libre, serif" }}>רשות כניסה</div>
      </div>

      {/* Details */}
      <div className="space-y-2 text-sm mb-3">
        <Row label="נאמען" value={pass.talmidName || "—"} bold />
        <Row label="כיתה" value={pass.klasName || "—"} />
        <div className="border-t border-dashed border-slate-400 my-2"></div>
        <div>
          <div className="text-slate-600 text-xs mb-0.5">דאטום:</div>
          <div className="font-bold text-sm leading-tight">{formatHebrewDate(pass.date)}</div>
        </div>
        <Row label="צייט" value={format12HourHeb(pass.time)} bold />
      </div>

      {/* Reason */}
      <div className="border-t border-dashed border-slate-400 pt-2 mb-3">
        <div className="text-xs text-slate-600 mb-1">סיבה:</div>
        <div className="text-sm font-medium min-h-[1.2em]">{pass.reason || "—"}</div>
        {(() => {
          const cat = pass.reasonCategory || getReasonCategory(pass.reason, settings?.commonReasons);
          if (!cat) return null;
          const isJustified = cat === CATEGORY_JUSTIFIED;
          return (
            <div
              className={`mt-2 border-2 text-center text-sm font-bold py-1 px-2 rounded ${
                isJustified ? "border-black" : "border-black bg-black text-white"
              }`}
              style={{ fontFamily: "Frank Ruhl Libre, serif" }}
            >
              {CATEGORY_LABEL_HE[cat]}
            </div>
          );
        })()}
      </div>

      {/* Signature */}
      <div className="border-t-2 border-dashed border-black pt-3 mt-4">
        <div className="text-xs text-slate-600 mb-1">אויפגענומען דורך:</div>
        <div className="text-sm font-bold">{pass.signedBy || "—"}</div>
        {showSig && <div className="mt-4 border-t border-black pt-1 text-center text-xs">חתימה</div>}
      </div>

      {/* Custom footer */}
      {footer && (
        <div className="border-t border-dashed border-slate-400 pt-2 mt-3 text-xs text-center text-slate-700">
          {footer}
        </div>
      )}

      {/* Footer */}
      <div className="text-center text-[10px] text-slate-500 mt-4 pt-3 border-t border-dashed border-black" dir="rtl">
        <div>{formatHebrewDate(created)} · {format12HourHeb(created)}</div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-slate-600">{label}:</span>
      <span className={`text-left break-words ${bold ? "font-bold" : ""}`}>{value}</span>
    </div>
  );
}
